import React, { useState } from 'react';
import { Box, Typography, Chip, Tabs, Tab } from '@mui/material';
import { CandidateInfoTab } from './CandidateInfoTab';
import { AssessmentSection } from './AssessmentSection';
import { CandidateActionBar } from './CandidateActionBar';
import type { Candidate } from '@components/ui/dashboard/ApplicantReview/types/candidate';
import { OfferApplicantStatus } from './../../../../types/OfferApplicantStatus.interface';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './CandidateDetailView.translations.json';
import statusTranslations from '../../offer-applicant/ApplicationStatus/translations.json';
import { inputStatusColorMap } from '@services/offerApplicant/offerApplicantBadgeStatus';

type SupportedLanguage = keyof typeof translations;
type SupportedStatusLanguage = keyof typeof statusTranslations;

interface CandidateDetailViewProps {
  defaultStatus?: OfferApplicantStatus;
  candidate: Candidate;
  onValidate?: () => void;
  onPending?: () => void;
  onReject?: () => void;
  onOfferSent?: () => void;
  onContractSent?: () => void;
  onHired?: () => void;
}

export const CandidateDetailView: React.FC<CandidateDetailViewProps> = ({
  defaultStatus,
  candidate,
  onValidate = () => {},
  onPending = () => {},
  onReject = () => {},
  onOfferSent,
  onContractSent,
  onHired,
}) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const statusContent = statusTranslations[language as SupportedStatusLanguage] || statusTranslations.en;
  const statusLabel =
    statusContent.statuses[defaultStatus as keyof typeof statusContent.statuses] || defaultStatus;
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Box flex={1} p={4} sx={{ height: '100vh', overflowY: 'auto', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="#FFFFFF">
            {candidate.name}
          </Typography>
          <Typography variant="subtitle1" color="#8C939D">
            {candidate.role}
          </Typography>
        </Box>
        {candidate.status && (
          <Chip
            label={statusLabel}
            sx={{
              backgroundColor: '#0F2A1D',
              color: '#34D399',
              border: '1px solid #165B3E',
              fontWeight: 'bold',
              fontSize: '0.7rem',
              letterSpacing: 0.5,
              ...inputStatusColorMap[defaultStatus as any],
            }}
          />
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, newValue) => setTabIndex(newValue)}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            textTransform: 'none',
            color: '#8C939D',
            borderRadius: 2,
            minHeight: 36,
            px: 2,
            mr: 1,
            fontSize: '0.85rem',
            '&.Mui-selected': {
              color: '#FFFFFF',
              backgroundColor: '#1F2229',
            },
          },
        }}
      >
        <Tab label={content.information} />
        <Tab label={content.general} />
        <Tab label={content.technical} />
      </Tabs>

      {/* Tab Panels */}
      {tabIndex === 0 && <CandidateInfoTab candidate={candidate} />}
      {tabIndex === 1 && <AssessmentSection questions={candidate.generalAssessments} />}
      {tabIndex === 2 && <AssessmentSection questions={candidate.technicalAssessments} />}
      {[
        OfferApplicantStatus.SHORTLISTED_BY_TECHNICAL_QUESTIONNAIRE,
        OfferApplicantStatus.ACCEPTED_AFTER_FINAL_REVIEW,
        OfferApplicantStatus.REJECTED_AFTER_FINAL_REVIEW,
        OfferApplicantStatus.UNDER_FINAL_REVIEW,
        OfferApplicantStatus.OFFER_ACCEPTED_BY_CANDIDATE,
        OfferApplicantStatus.CONTRACT_SENT,
      ].includes(candidate.status as any) && (
        <>
          {/* Action Footer */}
          <CandidateActionBar
            onValidate={onValidate}
            onPending={onPending}
            onReject={onReject}
            onOfferSent={onOfferSent}
            onContractSent={onContractSent}
            onHired={onHired}
            status={candidate.status as any}
          />
        </>
      )}
    </Box>
  );
};